/**
 * Add machine, download first (WP-03, ADR 0042 decision 1 "download, run, approve"): which file
 * this browser's computer needs, what the other systems are, and the demo data. Pure, so the
 * dialog's choices are unit-tested.
 */
import { INSTALL_SHELL_LABEL, RELEASE_TARGETS, RELEASE_TARGET_LABEL, installOneLiner } from '@orgus/protocol';
import type { AgentDownload, AgentDownloads, InstallShell, ReleaseTarget } from '@orgus/protocol';

/** The IT guide: allow-list, install paths, network and silent deployment (WP-13). */
export const IT_DEPLOYMENT_DOC = 'docs/runbooks/it-allow-list.md';

/** What the browser says about the computer it runs on. */
export interface BrowserFacts {
  userAgent: string;
  /** navigator.userAgentData.platform, when the browser has it ("Windows", "macOS", "Linux"). */
  platform?: string;
  /** navigator.userAgentData.mobile. */
  mobile?: boolean;
  /** High-entropy architecture ("x86", "arm"), when the browser gave it. */
  architecture?: string;
  /** The viewport is phone-sized (below the 768 px breakpoint). */
  narrow?: boolean;
}

/** Either the release target this computer needs, or a phone (send the link to a computer instead). */
export type Platform = { kind: 'computer'; target: ReleaseTarget | null } | { kind: 'phone'; target: ReleaseTarget | null };

/** The computer this browser runs on, from its user agent: a best guess, so every other system stays one click away. */
export function detectPlatform(f: BrowserFacts): Platform {
  const ua = f.userAgent;
  const plat = (f.platform ?? '').toLowerCase();
  const arch = (f.architecture ?? '').toLowerCase();
  const phoneUa = f.mobile === true || /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  let target: ReleaseTarget | null = null;
  if (!phoneUa) {
    if (plat === 'windows' || /Windows/i.test(ua)) target = arch === 'arm' || /ARM64|aarch64/i.test(ua) ? 'windows-arm64' : 'windows-amd64';
    // A Mac browser reports Intel either way; the architecture hint, when given, says which. Most Macs sold since 2020 are Apple silicon.
    else if (plat === 'macos' || /Macintosh|Mac OS X/i.test(ua)) target = arch === 'x86' ? 'darwin-amd64' : 'darwin-arm64';
    else if (plat === 'linux' || plat === 'chrome os' || /Linux|X11|CrOS/i.test(ua)) target = arch === 'arm' || /aarch64|arm64/i.test(ua) ? 'linux-arm64' : 'linux-amd64';
  }
  return phoneUa || f.narrow ? { kind: 'phone', target } : { kind: 'computer', target };
}

/** The primary file for this computer, and every other system in a stable order. */
export function pickDownloads(downloads: readonly AgentDownload[], platform: Platform): { primary: AgentDownload | null; others: AgentDownload[] } {
  const sorted = [...downloads].sort((a, b) => RELEASE_TARGETS.indexOf(a.target) - RELEASE_TARGETS.indexOf(b.target));
  const primary = platform.kind === 'computer' && platform.target ? (sorted.find((d) => d.target === platform.target) ?? null) : null;
  // Windows, then macOS, then Linux: the order people look for them.
  const order: ReleaseTarget[] = ['windows-amd64', 'windows-arm64', 'darwin-arm64', 'darwin-amd64', 'linux-amd64', 'linux-arm64'];
  const others = sorted.filter((d) => d !== primary).sort((a, b) => order.indexOf(a.target) - order.indexOf(b.target));
  return { primary, others };
}

/** "Download for Windows (x64)". */
export const downloadLabel = (target: ReleaseTarget): string => `Download for ${RELEASE_TARGET_LABEL[target]}`;

/** Whatever the hub sent, shown as a bare domain: never a scheme, a port or a path. */
export function bareDomain(domain: string): string {
  const d = domain.trim().replace(/^[a-z]+:\/\//i, '').replace(/[/?#].*$/, '');
  return d.startsWith('[') ? d.replace(/\]:\d+$/, ']') : d.replace(/:\d+$/, '');
}

/** The link the phone sends to a computer: this console's Add machine, which picks the right file there. */
export function downloadPageUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/machines?add=1`;
}

/** A mailto: link that sends the download page to the person's own inbox. */
export function mailtoDownload(origin: string): string {
  const subject = 'Add this computer to Orgus';
  const body = `Open this on the computer you want to add, then download the Orgus agent:\n${downloadPageUrl(origin)}`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * The one command that installs the downloaded agent and pairs it with a local hub (AgentDownloads.pairWith):
 * run where the file was saved, in PowerShell on Windows or a terminal elsewhere. The token-free
 * pairing of ADR 0042: the computer makes its own key and asks to join; a person approves it.
 * `words`: no browser on that computer (a server or VM), so it shows three words to type instead.
 * On Windows the pipe makes PowerShell wait for the agent (a windowless program it would not wait for).
 */
export function pairCommand(o: { file: string; target: ReleaseTarget; url: string; words?: boolean }): string {
  const args = `install --pair${o.words ? ' --words' : ''} --hub ${o.url}`;
  if (o.target.startsWith('windows')) return `.\\${o.file} ${args} | Out-Host`;
  return `chmod +x ./${o.file} && ./${o.file} ${args}`;
}

/** The shell a one-line install runs in on a target: PowerShell on Windows, sh elsewhere (ADR 0100). */
export const installShellFor = (target: ReleaseTarget): InstallShell => (target.startsWith('windows') ? 'powershell' : 'sh');

/**
 * The one-line install for a target, next to the download (ADR 0100): the script comes from the
 * fixed release origin, checks the binary's sha256 and runs install --pair. A local hub
 * (AgentDownloads.pairWith) is passed as --hub; null when its address is not one the command can carry.
 */
export function terminalInstallCommand(o: { target: ReleaseTarget; hubUrl?: string }): string | null {
  try {
    return installOneLiner(installShellFor(o.target), o.hubUrl ? ['--hub', o.hubUrl] : []);
  } catch {
    return null;
  }
}

/** All three one-line installs, for the command-line disclosure: label and command. */
export function allInstallCommands(hubUrl?: string): { shell: InstallShell; label: string; command: string }[] {
  const out: { shell: InstallShell; label: string; command: string }[] = [];
  for (const shell of ['sh', 'powershell', 'cmd'] as const) {
    try {
      out.push({ shell, label: INSTALL_SHELL_LABEL[shell], command: installOneLiner(shell, hubUrl ? ['--hub', hubUrl] : []) });
    } catch {
      // an address the command cannot carry: the download and the pair command still work
    }
  }
  return out;
}

const MB = 1024 * 1024;

/** Demo data: a local hub behind HTTPS on a private network (?add=local). */
const LOCAL_HUB = 'https://orgus.tail4c2e.ts.net';

/** Demo data: the newest signed release, as the hub lists it. */
export function mockDownloads(o: { unavailable?: boolean; local?: boolean } = {}): AgentDownloads {
  if (o.unavailable) {
    return {
      version: null,
      publisher: 'Orgus',
      signature: null,
      server: { domain: 'app.orgus.ai', reachable: true },
      downloads: [],
      unavailable: {
        what: 'Download not available yet',
        why: 'This server has no signed release of the Orgus agent to hand out.',
        next: 'An admin adds a signed release to the release folder of this server (ORGUS_RELEASES_DIR), then Add machine offers it.',
        code: 'no-release-feed',
      },
    };
  }
  const size: Record<ReleaseTarget, number> = {
    'darwin-amd64': 11.2 * MB,
    'darwin-arm64': 10.6 * MB,
    'linux-amd64': 10.4 * MB,
    'linux-arm64': 9.9 * MB,
    'windows-amd64': 10.5 * MB,
    'windows-arm64': 10.1 * MB,
  };
  return {
    version: '0.9.1',
    publisher: 'Orgus',
    signature: { keyId: '22ef0ca6455b7bc1', test: false },
    server: { domain: o.local ? LOCAL_HUB.replace(/^https:\/\//, '') : 'app.orgus.ai', reachable: true },
    ...(o.local ? { pairWith: { url: LOCAL_HUB } } : {}),
    downloads: RELEASE_TARGETS.map((target) => ({
      target,
      file: `orgus-node-agent-${target}${target.startsWith('windows') ? '.exe' : ''}`,
      size: Math.round(size[target]),
      sha256: '9f2c4e1a7b3d5f6e8a0c2e4f6a8b0d2f4e6a8c0e2f4a6b8d0f2e4a6c8e0a2b4d',
      url: `/api/fleet/downloads/${target}`,
    })),
    unavailable: null,
  };
}
